import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';
import {
  Typography, Button, Input, Tag, Tooltip, Alert, Modal, Space, Table, Steps,
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined, ReloadOutlined,
  UploadOutlined, DownloadOutlined, CheckCircleOutlined, ExclamationCircleOutlined,
  RightOutlined,
} from '@ant-design/icons';
import { api } from '../../services/api';
import { SkStyleTag, skeletonColumns, skeletonRows } from '../../shared/components/TableSkeleton';
import { useClient } from '../../context/ClientContext';
import { resolveImgUrl } from '../../templates/4/utils';

const { Text } = Typography;

const CircleBtn = ({ icon, onClick, danger, title, disabled }) => (
  <Tooltip title={title}>
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        width: 32,
        height: 32,
        border: `1px solid ${danger ? '#fca5a5' : '#e5e7eb'}`,
        borderRadius: '50%',
        background: '#fff',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: disabled ? 'not-allowed' : 'pointer',
        color: danger ? '#ef4444' : '#374151',
        opacity: disabled ? 0.45 : 1,
        transition: 'border-color 0.15s, color 0.15s',
        fontSize: 14,
        flexShrink: 0,
      }}
      onMouseEnter={(e) => {
        if (!disabled) {
          e.currentTarget.style.borderColor = danger ? '#ef4444' : '#374151';
        }
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = danger ? '#fca5a5' : '#e5e7eb';
      }}
    >
      {icon}
    </button>
  </Tooltip>
);

export const AdminProductos = () => {
  const { clientId, theme , clientPath} = useClient();
  const primary = theme?.primaryColor || '#0f172a';
  const primaryText = theme?.primaryTextColor || '#fff';
  const navigate = useNavigate();
  const isMobile = useMediaQuery({ query: '(max-width: 768px)' });
  const [productos, setProductos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // paginación + búsqueda — refs para evitar stale closures en cargar()
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [total, setTotal] = useState(0);
  const qRef = useRef('');
  const pageRef = useRef(1);
  const pageSizeRef = useRef(25);
  const searchTimer = useRef(null);

  // importar
  const [importModal, setImportModal] = useState(false);
  const [importStep, setImportStep] = useState(0); // 0=subir, 1=preview, 2=procesando/resultado
  const [importFile, setImportFile] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importPreview, setImportPreview] = useState([]);
  const [importError, setImportError] = useState(null);
  const [importJob, setImportJob] = useState(null); // { job_id, total }
  const [importResult, setImportResult] = useState(null); // job completado
  const pollRef = useRef(null);
  const fileInputRef = useRef(null);

  const cargar = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.admin.catalogo.getProductos({
        q: qRef.current, page: pageRef.current, limit: pageSizeRef.current,
      });
      if (res.status === 'success') {
        setProductos(res.data?.items || []);
        setTotal(res.data?.total || 0);
      } else {
        setError(res.message || 'Error al cargar productos');
      }
    } catch {
      setError('Error al conectar con el servidor');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const handleQChange = (val) => {
    setQ(val); qRef.current = val;
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      pageRef.current = 1; setPage(1);
      cargar();
    }, 400);
  };

  const handlePageChange = (newPage, newSize) => {
    pageRef.current = newPage; setPage(newPage);
    pageSizeRef.current = newSize; setPageSize(newSize);
    cargar();
  };

  const handleDelete = async () => {
    if (!confirmDel) return;
    setDeleting(true);
    try {
      await api.admin.catalogo.eliminarProducto(confirmDel.id);
      setConfirmDel(null);
      cargar();
    } finally {
      setDeleting(false);
    }
  };

  const detenerPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  const abrirImport = () => {
    setImportModal(true);
    setImportStep(0);
    setImportFile(null);
    setImportPreview([]);
    setImportError(null);
    setImportJob(null);
    setImportResult(null);
  };

  const cerrarImport = () => {
    if (importLoading) return;
    detenerPolling();
    setImportModal(false);
  };

  const descargarPlantilla = async () => {
    try {
      const res = await api.admin.catalogo.descargarPlantilla();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'plantilla_productos.xlsx';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      // silencioso, el navegador mostrará error de red si falla
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportFile(file);
    setImportError(null);
  };

  const handlePreview = async () => {
    if (!importFile) return;
    setImportLoading(true);
    setImportError(null);
    try {
      const res = await api.admin.catalogo.previewImportacion(importFile);
      if (res.status === 'success') {
        setImportPreview(res.data || []);
        setImportStep(1);
      } else {
        setImportError(res.message || 'Error al leer el archivo');
      }
    } catch {
      setImportError('Error al conectar con el servidor');
    } finally {
      setImportLoading(false);
    }
  };

  const handleImportar = async () => {
    setImportLoading(true);
    setImportError(null);
    try {
      const res = await api.admin.catalogo.importarProductos(importFile);
      if (res.status === 'procesando') {
        setImportJob(res.data); // { job_id, total }
        setImportStep(2);
        // Polling cada 1.5s hasta completado o error
        pollRef.current = setInterval(async () => {
          try {
            const estado = await api.admin.catalogo.importarEstado(res.data.job_id);
            if (estado?.data?.status === 'completado' || estado?.data?.status === 'error') {
              detenerPolling();
              setImportResult(estado.data);
              if (estado.data.status === 'completado') cargar();
            }
          } catch { /* red momentáneamente caída, reintentará */ }
        }, 1500);
      } else {
        setImportError(res.message || 'Error al importar');
      }
    } catch {
      setImportError('Error al conectar con el servidor');
    } finally {
      setImportLoading(false);
    }
  };

  const filasValidas  = importPreview.filter(f => !f.error);
  const filasConError = importPreview.filter(f =>  f.error);

  const previewColumns = [
    { title: 'Fila', dataIndex: 'fila', width: 55 },
    { title: 'Nombre', dataIndex: 'nombre', ellipsis: true, width: 180 },
    { title: 'Código', dataIndex: 'codigo', width: 100, render: v => v || '—' },
    { title: 'P. Minorista', dataIndex: 'precio_minorista', width: 110, render: v => v ? `$${v}` : '—' },
    { title: 'P. Mayorista', dataIndex: 'precio_mayorista', width: 110, render: v => v ? `$${v}` : '—' },
    { title: 'Stock', dataIndex: 'stock', width: 65 },
    { title: 'Imágenes', dataIndex: 'imagenes', width: 85, render: v => v?.length ? `${v.length} URL${v.length > 1 ? 's' : ''}` : '—' },
    {
      title: 'Estado', width: 85, render: (_, r) => r.error
        ? <Tag color="error" icon={<ExclamationCircleOutlined />}>Error</Tag>
        : <Tag color="success" icon={<CheckCircleOutlined />}>OK</Tag>
    },
    { title: 'Error', dataIndex: 'error', ellipsis: true, render: v => v ? <span style={{ color: '#ef4444', fontSize: 12 }}>{v}</span> : null },
  ];

  const precioDisplay = (precios) => {
    if (!precios?.length) return '—';
    return precios.map(p => `$${Number(p.precio).toFixed(2)}`).join(' · ');
  };

  const resolveProductImg = (p) => {
    const adj = p.adjuntos?.[0];
    if (adj?.adjunto) {
      const src = adj.adjunto;
      return (src.startsWith('http') || src.startsWith('/'))
        ? resolveImgUrl(src)
        : resolveImgUrl(`/uploads/productos/${adj.id_tabla}/${src}`);
    }
    if (p.imagen) return resolveImgUrl(p.imagen);
    return null;
  };

  const [togglingVisible, setTogglingVisible] = useState(new Set());
  const toggleVisible = async (p) => {
    if (togglingVisible.has(p.id)) return;
    setTogglingVisible(prev => new Set(prev).add(p.id));
    const nuevoValor = !p.visible;
    setProductos(prev => prev.map(x => x.id === p.id ? { ...x, visible: nuevoValor } : x));
    try {
      await api.admin.catalogo.editarProducto(p.id, { visible: nuevoValor });
    } catch {
      setProductos(prev => prev.map(x => x.id === p.id ? { ...x, visible: p.visible } : x));
    } finally {
      setTogglingVisible(prev => { const n = new Set(prev); n.delete(p.id); return n; });
    }
  };

  const hasVariantes = (p) => p.variantes?.length > 0;

  const [expandidos, setExpandidos] = useState(new Set());
  const toggleExpand = (id) => setExpandidos(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const [togglingDestacado, setTogglingDestacado] = useState(new Set());
  const toggleDestacado = async (p) => {
    if (togglingDestacado.has(p.id)) return;
    setTogglingDestacado(prev => new Set(prev).add(p.id));
    const nuevoValor = !p.destacado;
    setProductos(prev => prev.map(x => x.id === p.id ? { ...x, destacado: nuevoValor } : x));
    try {
      await api.admin.catalogo.editarProducto(p.id, { destacado: nuevoValor });
    } catch {
      setProductos(prev => prev.map(x => x.id === p.id ? { ...x, destacado: p.destacado } : x));
    } finally {
      setTogglingDestacado(prev => { const n = new Set(prev); n.delete(p.id); return n; });
    }
  };

  const productColumns = [
    {
      key: 'destacado', width: 36,
      render: (_, p) => (
        <Tooltip title={p.destacado ? 'Quitar de destacados' : 'Marcar como destacado'}>
          <button
            type="button"
            onClick={() => toggleDestacado(p)}
            style={{
              background: 'none', border: 'none', cursor: 'pointer', padding: 0,
              fontSize: 17, lineHeight: 1, color: p.destacado ? '#f59e0b' : '#d1d5db',
              transition: 'color 0.15s',
            }}
            onMouseEnter={e => { if (!p.destacado) e.currentTarget.style.color = '#fbbf24'; }}
            onMouseLeave={e => { if (!p.destacado) e.currentTarget.style.color = '#d1d5db'; }}
          >★</button>
        </Tooltip>
      ),
    },
    {
      title: 'Producto', key: 'nombre', minWidth: 220,
      render: (_, p) => {
        const imgSrc = resolveProductImg(p);
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 44, height: 44, borderRadius: 8, flexShrink: 0,
              background: '#f3f4f6', overflow: 'hidden',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              {imgSrc ? (
                <img src={imgSrc} alt={p.nombre} style={{ width: '100%', height: '100%', objectFit: 'contain' }} onError={e => { e.target.style.display = 'none'; }} />
              ) : (
                <span style={{ fontSize: 20 }}>📦</span>
              )}
            </div>
            <div style={{ minWidth: 0 }}>
              <div
                style={{ fontWeight: 600, fontSize: 14, color: primary, cursor: 'pointer', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 240 }}
                onClick={() => navigate(clientPath(`/admin/productos/${p.id}`))}
              >
                {p.nombre}
              </div>
              {p.codigo && <div style={{ fontSize: 12, color: '#9ca3af' }}>SKU: {p.codigo}</div>}
              {hasVariantes(p) && <div style={{ fontSize: 12, color: '#9ca3af' }}>{p.variantes.length} variante{p.variantes.length !== 1 ? 's' : ''}</div>}
            </div>
          </div>
        );
      },
    },
    {
      title: 'Stock', key: 'stock', width: 90,
      render: (_, p) => hasVariantes(p) ? (
        <Text style={{ fontWeight: 500, color: '#111827' }}>
          {p.variantes.reduce((sum, v) => sum + (v.stock || 0), 0)}
          {p.unidad && <Text type="secondary" style={{ fontSize: 12 }}> {p.unidad}</Text>}
        </Text>
      ) : (
        <Text style={{ fontWeight: 500, color: p.stock_minimo > 0 && p.stock <= p.stock_minimo ? '#ef4444' : '#111827' }}>
          {p.stock}{p.unidad && <Text type="secondary" style={{ fontSize: 12 }}> {p.unidad}</Text>}
        </Text>
      ),
    },
    {
      title: 'Precio', key: 'precio', width: 150,
      render: (_, p) => (
        <Text type="secondary" style={{ fontSize: 13 }}>{precioDisplay(p.precios)}</Text>
      ),
    },
    {
      title: 'Estado', key: 'estado', width: 140,
      render: (_, p) => {
        const visible = p.visible;
        const activo = p.activo !== false;
        const statusColor = !activo ? '#ef4444' : visible ? '#16a34a' : '#9ca3af';
        const statusBg = !activo ? '#fef2f2' : visible ? '#f0fdf4' : '#f9fafb';
        const statusLabel = !activo ? 'Inactivo' : visible ? 'Visible' : 'Oculto';
        const loadingToggle = togglingVisible.has(p.id);
        return (
          <Tooltip title={activo ? (visible ? 'Click para ocultar del catálogo' : 'Click para mostrar en el catálogo') : 'Producto inactivo'}>
            <span
              onClick={() => activo && toggleVisible(p)}
              style={{
                display: 'inline-flex', alignItems: 'center',
                padding: '3px 10px', borderRadius: 20,
                border: `1.5px solid ${statusColor}`,
                background: statusBg,
                color: statusColor,
                fontSize: 12, fontWeight: 600,
                whiteSpace: 'nowrap',
                cursor: activo ? 'pointer' : 'default',
                opacity: loadingToggle ? 0.6 : 1,
                transition: 'opacity 0.15s, border-color 0.15s',
                userSelect: 'none',
              }}
            >
              {statusLabel}
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: '', key: 'acciones', width: 80, align: 'center',
      render: (_, p) => (
        <Space size={6}>
          <CircleBtn icon={<EditOutlined />} title="Editar" onClick={() => navigate(clientPath(`/admin/productos/${p.id}`))} />
          <CircleBtn icon={<DeleteOutlined />} title="Desactivar" danger disabled={!p.activo} onClick={() => setConfirmDel(p)} />
        </Space>
      ),
    },
  ];

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <Text strong style={{ fontSize: 28, lineHeight: 1.2 }}>Productos</Text>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Tooltip title="Actualizar">
            <Button type="text" icon={<ReloadOutlined />} onClick={cargar} loading={loading} />
          </Tooltip>
          <Button icon={<UploadOutlined />} onClick={abrirImport}>
            Importar
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate(clientPath('/admin/productos/nuevo'))}
            style={{ background: primary, borderColor: primary, color: primaryText }}
          >
            Nuevo producto
          </Button>
        </div>
      </div>

      {/* Search */}
      <Input
        placeholder="Buscar por nombre o código..."
        value={q}
        onChange={(e) => handleQChange(e.target.value)}
        onClear={() => handleQChange('')}
        prefix={<SearchOutlined style={{ color: '#9ca3af' }} />}
        allowClear
        size="large"
        style={{ marginBottom: 12, borderRadius: 6 }}
      />

      {error && <Alert type="error" message={error} style={{ marginBottom: 16 }} />}

      {/* Count */}
      {!loading && !error && (
        <Text type="secondary" style={{ display: 'block', marginBottom: 12, fontSize: 13 }}>
          {total} producto{total !== 1 ? 's' : ''}
        </Text>
      )}

      <style>{`
        .productos-table .ant-table-expanded-row > td {
          padding: 0 !important;
        }
        .productos-table .ant-table-expanded-row .ant-table-cell {
          padding: 0 !important;
        }
      `}</style>

      {/* Table */}
      <SkStyleTag />
      <Table
        className="productos-table"
        dataSource={loading ? skeletonRows(9) : productos}
        rowKey={loading ? 'key' : 'id'}
        scroll={{ x: 'max-content' }}
        pagination={loading ? false : {
          current: page,
          pageSize,
          total,
          onChange: handlePageChange,
          onShowSizeChange: handlePageChange,
          showSizeChanger: true,
          pageSizeOptions: ['10', '25', '50'],
          showTotal: (t, [from, to]) => `${from}–${to} de ${t}`,
          size: 'small',
          style: { padding: '8px 16px' },
        }}
        locale={{
          emptyText: q ? 'No hay productos que coincidan con la búsqueda.' : 'Todavía no hay productos cargados.',
        }}
        expandable={loading ? undefined : {
          expandedRowKeys: [...expandidos],
          onExpand: (expanded, record) => toggleExpand(record.id),
          rowExpandable: (record) => hasVariantes(record),
          columnWidth: 40,
          expandIcon: ({ expanded, onExpand, record }) =>
            hasVariantes(record) ? (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onExpand(record, e); }}
                style={{
                  width: 20, height: 20, border: 'none', background: 'transparent',
                  cursor: 'pointer', padding: 0, display: 'inline-flex',
                  alignItems: 'center', justifyContent: 'center', color: '#9ca3af',
                }}
              >
                <RightOutlined style={{ fontSize: 10, transition: 'transform 0.15s', transform: expanded ? 'rotate(90deg)' : 'none' }} />
              </button>
            ) : <span style={{ display: 'inline-block', width: 20 }} />,
          expandedRowRender: (p) => (
            <div style={{ borderLeft: `3px solid ${primary}33`, marginLeft: 76, background: '#f9fafb' }}>
              {p.variantes.map((v, vi) => (
                <div key={v.id} style={{
                  display: 'grid',
                  /* nombre(flex) + stock(90) + precio(150) + estado(140) + acciones(80) */
                  gridTemplateColumns: 'minmax(180px, 1fr) 90px 150px 140px 80px',
                  alignItems: 'center',
                  padding: '8px 16px 8px 20px',
                  borderTop: vi === 0 ? 'none' : '1px solid #eef0f8',
                }}>
                  {/* Variante label */}
                  <div>
                    <Text style={{ fontSize: 13, color: '#374151' }}>{v.label || '—'}</Text>
                    {v.sku && <Text type="secondary" style={{ fontSize: 11, marginLeft: 8 }}>SKU: {v.sku}</Text>}
                  </div>
                  {/* Stock — columna Stock */}
                  <Text style={{ fontSize: 13, fontWeight: 500, color: v.stock <= 0 ? '#ef4444' : '#111827' }}>
                    {v.stock}{p.unidad && <Text type="secondary" style={{ fontSize: 11 }}> {p.unidad}</Text>}
                  </Text>
                  {/* Precio — columna Precio */}
                  <div>
                    {(v.precios?.length > 0 ? v.precios : (p.precios || [])).map(pr => (
                      <Text key={pr.id_tipo_precio} style={{ fontSize: 12, display: 'block' }}>
                        <Text type="secondary" style={{ fontSize: 11 }}>{pr.tipo_nombre}: </Text>
                        ${Number(pr.precio).toFixed(2)}
                      </Text>
                    ))}
                  </div>
                  {/* Estado + acciones vacíos */}
                  <div /><div />
                </div>
              ))}
            </div>
          ),
        }}
        columns={loading ? skeletonColumns(productColumns, { avatarIndex: 1, twoLineIndexes: [1] }) : productColumns}
        style={{ background: '#fff', borderRadius: 8 }}
        rowStyle={{ verticalAlign: 'middle' }}
      />

      {/* Modal confirm */}
      <Modal
        open={!!confirmDel}
        title="¿Desactivar producto?"
        onCancel={() => !deleting && setConfirmDel(null)}
        footer={
          <Space>
            <Button onClick={() => setConfirmDel(null)} disabled={deleting}>Cancelar</Button>
            <Button danger onClick={handleDelete} loading={deleting}>
              {deleting ? 'Desactivando...' : 'Desactivar'}
            </Button>
          </Space>
        }
      >
        <Text>
          <strong>{confirmDel?.nombre}</strong> quedará inactivo y no aparecerá en el catálogo. Podés reactivarlo editándolo.
        </Text>
      </Modal>

      {/* Modal importar */}
      <Modal
        open={importModal}
        title="Importar productos desde Excel"
        onCancel={cerrarImport}
        width={importStep === 1 ? 820 : 520}
        footer={null}
        destroyOnClose
      >
        <Steps
          current={importStep}
          size="small"
          style={{ marginBottom: 24 }}
          items={[
            { title: 'Cargar archivo' },
            { title: 'Revisar' },
            { title: 'Resultado' },
          ]}
        />

        {/* Paso 0: subir archivo */}
        {importStep === 0 && (
          <div>
            <div style={{
              background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8,
              padding: 16, marginBottom: 16,
            }}>
              <Text strong style={{ display: 'block', marginBottom: 6 }}>Plantilla</Text>
              <Text type="secondary" style={{ fontSize: 13 }}>
                Descargá la plantilla Excel, completá los productos y luego subí el archivo.
              </Text>
              <div style={{ marginTop: 10 }}>
                <Button icon={<DownloadOutlined />} onClick={descargarPlantilla} size="small">
                  Descargar plantilla .xlsx
                </Button>
              </div>
            </div>

            <Text strong style={{ display: 'block', marginBottom: 8 }}>Archivo a importar</Text>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              style={{ display: 'none' }}
              onChange={handleFileSelect}
            />
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 16 }}>
              <Button icon={<UploadOutlined />} onClick={() => fileInputRef.current?.click()}>
                Seleccionar archivo
              </Button>
              {importFile && (
                <Text type="secondary" style={{ fontSize: 13 }}>{importFile.name}</Text>
              )}
            </div>

            {importError && <Alert type="error" message={importError} style={{ marginBottom: 12 }} />}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Button onClick={cerrarImport}>Cancelar</Button>
              <Button
                type="primary"
                disabled={!importFile}
                loading={importLoading}
                onClick={handlePreview}
                style={{ background: primary, borderColor: primary, color: primaryText }}
              >
                Siguiente
              </Button>
            </div>
          </div>
        )}

        {/* Paso 1: preview */}
        {importStep === 1 && (
          <div>
            <div style={{ marginBottom: 12, display: 'flex', gap: 12 }}>
              <Tag color="success">{filasValidas.length} producto{filasValidas.length !== 1 ? 's' : ''} válido{filasValidas.length !== 1 ? 's' : ''}</Tag>
              {filasConError.length > 0 && (
                <Tag color="error">{filasConError.length} con error (no se importarán)</Tag>
              )}
            </div>
            <Table
              dataSource={importPreview}
              columns={previewColumns}
              rowKey="fila"
              size="small"
              pagination={{ pageSize: 10, showSizeChanger: false }}
              scroll={{ x: 600 }}
              rowClassName={r => r.error ? 'import-row-error' : ''}
            />
            {importError && <Alert type="error" message={importError} style={{ margin: '12px 0' }} />}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <Button onClick={() => { setImportStep(0); setImportError(null); }}>Volver</Button>
              <Button
                type="primary"
                disabled={filasValidas.length === 0}
                loading={importLoading}
                onClick={handleImportar}
                style={{ background: primary, borderColor: primary, color: primaryText }}
              >
                Importar {filasValidas.length} producto{filasValidas.length !== 1 ? 's' : ''}
              </Button>
            </div>
          </div>
        )}

        {/* Paso 2: procesando → resultado */}
        {importStep === 2 && (
          <div style={{ textAlign: 'center', padding: '16px 0' }}>
            {!importResult ? (
              // Procesando
              <div>
                <div style={{ marginBottom: 16 }}>
                  <div style={{
                    width: 48, height: 48, margin: '0 auto 12px',
                    border: '4px solid #e5e7eb', borderTopColor: primary,
                    borderRadius: '50%', animation: 'spin 0.8s linear infinite',
                  }} />
                  <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                </div>
                <Text strong style={{ display: 'block', fontSize: 16, marginBottom: 6 }}>
                  Importando productos...
                </Text>
                <Text type="secondary" style={{ fontSize: 13 }}>
                  {importJob?.total} producto{importJob?.total !== 1 ? 's' : ''} en proceso. Podés cerrar esta ventana y seguir trabajando.
                </Text>
                <div style={{ marginTop: 20 }}>
                  <Button onClick={cerrarImport}>Cerrar y continuar</Button>
                </div>
              </div>
            ) : importResult.status === 'completado' ? (
              // Completado
              <div>
                <CheckCircleOutlined style={{ fontSize: 48, color: '#22c55e', marginBottom: 12 }} />
                <Text strong style={{ display: 'block', fontSize: 18, marginBottom: 6 }}>
                  {importResult.creados} producto{importResult.creados !== 1 ? 's' : ''} importado{importResult.creados !== 1 ? 's' : ''}
                </Text>
                {importResult.errores?.length > 0 && (
                  <Alert
                    type="warning"
                    message={`${importResult.errores.length} fila${importResult.errores.length !== 1 ? 's' : ''} con error no se importaron`}
                    style={{ marginTop: 12, textAlign: 'left' }}
                  />
                )}
                <Button type="primary" onClick={cerrarImport} style={{ marginTop: 20, background: primary, borderColor: primary, color: primaryText }}>
                  Cerrar
                </Button>
              </div>
            ) : (
              // Error en el job
              <div>
                <ExclamationCircleOutlined style={{ fontSize: 48, color: '#ef4444', marginBottom: 12 }} />
                <Text strong style={{ display: 'block', fontSize: 16, marginBottom: 6, color: '#ef4444' }}>
                  Error en la importación
                </Text>
                <Text type="secondary">{importResult.mensaje}</Text>
                <div style={{ marginTop: 20 }}>
                  <Button onClick={cerrarImport}>Cerrar</Button>
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};
